﻿import {
	Button,
	Combobox,
	OptionGroup,
	Option,
	Text,
	Spinner,
	mergeClasses,
} from "@fluentui/react-components";
import { LexicalEditor } from "lexical";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { insertTemplate } from "../../molecules/htmlEditor/plugins/InsertTemplatePlugin";
import DropDown from "../../molecules/htmlEditor/ui/DropDown";
import { fetchFilledMessagingTemplateById } from "@/repository/queryFns";
import { QueueTemplateDto, TemplateType } from "@/repository/models/templates";
import { useStyle } from "./style";
import { Flex } from "@/components/atoms/flex";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PanelHeader } from "@/components/atoms/panelHeader";
import { Close32Regular } from "@/common/icons/closeIcon/Close32Regular";
import { DocumentOnePageSparkle20Regular } from "@fluentui/react-icons";
import {
	fetchMesagingQueueTemplates,
	MessagingQueueTemplatesKey,
} from "@/repository/queryFns/messagingQueueTemplates";
import { captureError } from "@/common/helpers/sentryHelper";
import { InsertType } from "@/common/types/lexicalEditor";
import { AiIndicator } from "@/components/atoms/aiIndicator";

interface TemplateProps {
	isEditable: boolean;
	editor: LexicalEditor | undefined;
	disabledTemplateTypes?: TemplateType[];
	aicLockedTemplateTypes?: TemplateType[];
	onTemplateApplied?: (isAi: boolean, insertType: InsertType) => void;
}

const insertAtCursorTemplateTypes = [
	TemplateType.Answer,
	TemplateType.AIAssistantResponse,
	TemplateType.Signature,
];

const aiTemplateTypes = [TemplateType.AIAssistantResponse, TemplateType.AIPrompt];

export const TemplateDropdown = (props: TemplateProps) => {
	const { isEditable, editor } = props;

	const intl = useIntl();
	const [hideDropdown, setHideDropdown] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");
	const [isTemplateLoading, setIsTemplateLoading] = useState(false);
	const [selectedId, setSelectedId] = useState<string>();
	const classes = useStyle();
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);
	const queryClient = useQueryClient();
	const queryKey = [MessagingQueueTemplatesKey];

	const fetchMesagingQueueTemplatesCallBack = useCallback(
		(_: any) => {
			return fetchMesagingQueueTemplates(currentInteraction?.queueId);
		},
		[queryKey]
	);
	const { data: templates } = useQuery<QueueTemplateDto[]>({
		queryKey: queryKey,
		queryFn: fetchMesagingQueueTemplatesCallBack,
		refetchOnMount: "always",
	});

	const getTemplateBodyAndInsertAtCursor = async (
		id: string
	): Promise<void> => {
		try {
			if (!currentInteraction) {
				return;
			}

			setHideDropdown(true);

			const template = await fetchFilledMessagingTemplateById(
				id,
				currentInteraction.id
			);
			const insertType = insertAtCursorTemplateTypes.includes(template.type)
				? InsertType.AtCursor
				: InsertType.Replacement;
			insertTemplate(editor, template.body, insertType);
			setSelectedId(template.id);
			props.onTemplateApplied?.(aiTemplateTypes.includes(template.type), insertType);
		} catch (err: any) {
			captureError(err, "Error when fetching templates");
			console.error("Error fetching template:", err);
		} finally {
			setIsTemplateLoading(false);
		}
	};

	const onTemplateSelect = useCallback((_: any, data: any) => {
		if (data.optionValue) {
			setIsTemplateLoading(true);
			getTemplateBodyAndInsertAtCursor(data.optionValue);
		}
	}, []);

	const filteredTemplates = useMemo<QueueTemplateDto[]>(() => {
		return templates
			? templates.filter((t) =>
					t.templateName.toLowerCase().includes(searchText.toLowerCase())
				)
			: [];
	}, [searchText, templates]);

	const templateOptionGroups = useMemo<
		Record<string, QueueTemplateDto[]>
	>(() => {
		const groupedTemplates = filteredTemplates.reduce(
			(groups, item) => {
				const typeName = TemplateType[item.templateType];
				groups[typeName] ??= [];
				groups[typeName]?.push(item);

				return groups;
			},
			{} as Record<string, QueueTemplateDto[]>
		);

		return Object.keys(groupedTemplates)
			.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
			.reduce(
				(sortedGroups, key) => {
					sortedGroups[key] = groupedTemplates[key] ?? [];
					return sortedGroups;
				},
				{} as Record<string, QueueTemplateDto[]>
			);
	}, [searchText, filteredTemplates]);

	const templateTypeName = useCallback(
		(typeAsString: string) => {
			return intl.formatMessage({
				id: `Interaction.EmailEditor.Templates.${typeAsString}`,
			});
		},
		[intl]
	);

	const hasSearchResults = useMemo(() => {
		return !searchText || filteredTemplates.length;
	}, [searchText, filteredTemplates]);

	const isTemplateTypeDisabled = useCallback(
		(tempalteType: TemplateType): boolean => {
			return props.disabledTemplateTypes?.includes(tempalteType) ?? false;
		},
		[props.disabledTemplateTypes]
	);

	const groupedOptions = useMemo<any>(() => {
		return Object.keys(templateOptionGroups).map((category) => {
			const options = templateOptionGroups[category]?.map(
				(item: QueueTemplateDto) => {
					const templateTypeIsDisabled = isTemplateTypeDisabled(
						item.templateType
					);
					const isAicLockedType = props.aicLockedTemplateTypes?.includes(item.templateType) ?? false;
						const optionTitle = templateTypeIsDisabled
							? intl.formatMessage({
									id: isAicLockedType
										? "Interaction.Buttons.FeatureNotAvailable"
										: "Interaction.EmailEditor.Templates.DisabledOnFirstOutbound",
								})
							: undefined;

					const isAiTemplate = aiTemplateTypes.includes(item.templateType);

					return (
						<Option
							key={item.templateId}
							value={item.templateId}
							text={item.templateName}
							disabled={templateTypeIsDisabled}
							title={optionTitle}
							className={mergeClasses(
								classes.option,
								selectedId === item.templateId && classes.selected
							)}
						>
							<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
								{item.templateName}
								{isAiTemplate && <AiIndicator mode="icon-only" />}
							</span>
						</Option>
					);
				}
			);

			return {
				label: templateTypeName(category),
				options: options,
			};
		});
	}, [templateOptionGroups, selectedId, isTemplateTypeDisabled]);

	return (
		<DropDown
			disabled={!isEditable}
			buttonClassName={classes.button}
			icon={
				isTemplateLoading ? (
					<Spinner size="tiny" />
				) : (
					<DocumentOnePageSparkle20Regular />
				)
			}
			stopCloseOnClickSelf={true}
			hideDropDown={hideDropdown}
			onClick={() => {
				setHideDropdown(false);
				queryClient.invalidateQueries({ queryKey: queryKey });
			}}
		>
			{
				<Flex column className={classes.container}>
					<PanelHeader
						left={
							<Text className={classes.headerTitle}>
								{intl.formatMessage({
									id: "Interaction.EmailEditor.PredefinedResponse",
								})}
							</Text>
						}
						right={
							<Button
								appearance="transparent"
								shape="circular"
								icon={<Close32Regular />}
								size="small"
								onClick={() => setHideDropdown(true)}
							/>
						}
						className={classes.panelHeader}
					/>
					<Combobox
						className={classes.comboBox}
						placeholder={intl.formatMessage({
							id: "Interaction.EmailEditor.TemplateInputPlaceholder",
						})}
						value={searchText}
						onChange={(event: ChangeEvent<HTMLInputElement>) => {
							setSearchText(event.target.value);
						}}
						onOptionSelect={onTemplateSelect}
						freeform
						inlinePopup
						open={true}
						disableAutoFocus={true}
					>
						{!hasSearchResults && (
							<Text className={classes.noResultText}>
								{intl.formatMessage({ id: "Contacts.SearchNoResults" })}
							</Text>
						)}
						{groupedOptions.map((group: any, index: any) => (
							<OptionGroup
								key={index}
								label={group.label}
								className={classes.optionGroup}
							>
								{group.options}
							</OptionGroup>
						))}
					</Combobox>
				</Flex>
			}
		</DropDown>
	);
};
